import {
	existsSync,
	readFileSync,
	mkdirSync,
	openSync,
	writeSync,
	closeSync,
	chmodSync,
	renameSync,
	unlinkSync,
} from 'node:fs';
import { dirname, join } from 'node:path';
import envPaths from 'env-paths';
import { CliError, ExitCode } from './errors';
import { printWarn } from './output';
import { currentChannel } from './channel';

export const API_KEY_ENV_VAR = 'WPORT_API_KEY';
export const KEY_PREFIX = 'wpk_live_';
// Server contract（spec §2.1）：wpk_live_ + 32 高熵字元。下限驗證擋手滑貼半截；
// 不驗上限——server 端才是 key 有效性的唯一權威。
const KEY_MIN_LENGTH = KEY_PREFIX.length + 32;

export interface Credentials {
	api_key: string;
	company_name: string;
	key_last4: string;
	saved_at: string;
}

/** 個人線 OAuth session（device flow 取得）。與 enterprise Credentials 各自獨立成一區。 */
export interface PersonalCredentials {
	access_token: string; // wpt_ 前綴
	refresh_token: string;
	expires_at: string; // ISO；access token 到期時刻（寫入時以 now + expires_in 算出）
	display_name: string;
	email: string;
	session_created_at: string; // ISO
}

export type KeySource = 'flag' | 'env' | 'file';

export interface ResolvedKey {
	key: string;
	source: KeySource;
}

/**
 * 檔案格式 v2（內部，不 export）：{ version: 2, enterprise?: Credentials, personal?: PersonalCredentials }。
 * 舊版 flat 格式（api_key 在頂層）讀入時視為 { version: 2, enterprise: <flat> }。
 * 任何 save 系列／delete 系列函式一律以此讀-改-寫，保留另一區不受影響。
 */
interface RawCredentialsFile {
	enterprise?: unknown;
	personal?: unknown;
}

const paths = envPaths('wport', { suffix: '' });

// 憑證檔名依 build channel 分檔:dev 寫 credentials-dev.json,避免切版時 token 互蓋 / 誤用打錯後端。
// fail-safe 與 channelBaseUrl 一致:僅 'dev' 走分檔,prod 與任何未知 channel 一律 credentials.json
// —— 既有使用者零遷移,且不把任意 channel 字串內插進檔名路徑。
export function getCredentialsPath(): string {
	const fileName = currentChannel() === 'dev' ? 'credentials-dev.json' : 'credentials.json';
	return join(paths.config, fileName);
}

export function isValidKeyFormat(key: string): boolean {
	return key.startsWith(KEY_PREFIX) && key.length >= KEY_MIN_LENGTH && !/\s/.test(key);
}

/** 任何輸出顯示 key 一律走這裡：只露末四碼。 */
export function maskKey(key: string): string {
	return `${KEY_PREFIX}••••${key.slice(-4)}`;
}

/**
 * 讀取原始檔案，拆成 enterprise/personal 兩個「未驗證」子物件。
 *
 * strict=true（給 load* 用）：JSON 壞掉或結構不對 → 丟 ConfigCorrupt（既有行為，讀路徑要能明確告警）。
 * strict=false（給 save 系列／delete 系列的讀-改-寫合併用）：JSON 壞掉 → 當成空檔處理，讓
 * save 可以放心覆寫重建（保住錯誤訊息裡「Run "wport enterprise login" to recreate it」的復原承諾）；
 * 另一區的內容在此只搬運、不解析驗證，避免「存 A 卻因為 B 區壞掉而炸開」的跨區污染。
 */
function readRawFile(strict: boolean): RawCredentialsFile {
	const path = getCredentialsPath();
	if (!existsSync(path)) return {};
	let parsed: unknown;
	try {
		parsed = JSON.parse(readFileSync(path, 'utf8'));
	} catch (err) {
		if (!strict) return {};
		throw new CliError(
			`Failed to read credentials at ${path}: ${(err as Error).message}. Run "wport enterprise login" to recreate it.`,
			ExitCode.ConfigCorrupt
		);
	}
	if (!parsed || typeof parsed !== 'object') {
		if (!strict) return {};
		throw new CliError(
			`Credentials file at ${path} is malformed. Run "wport enterprise login" to recreate it.`,
			ExitCode.ConfigCorrupt
		);
	}
	const raw = parsed as Record<string, unknown>;
	// 舊版 flat 格式偵測：v2 檔案不會在頂層放 api_key，看到就代表整份物件本身即 enterprise 區。
	if (typeof raw.api_key === 'string') {
		return { enterprise: raw };
	}
	return { enterprise: raw.enterprise, personal: raw.personal };
}

function parseEnterpriseCredentials(raw: unknown): Credentials {
	const path = getCredentialsPath();
	if (!raw || typeof raw !== 'object' || typeof (raw as Record<string, unknown>).api_key !== 'string') {
		throw new CliError(
			`Credentials file at ${path} is malformed. Run "wport enterprise login" to recreate it.`,
			ExitCode.ConfigCorrupt
		);
	}
	const r = raw as Record<string, unknown>;
	const apiKey = r.api_key as string;
	return {
		api_key: apiKey,
		company_name: typeof r.company_name === 'string' ? r.company_name : '',
		key_last4: typeof r.key_last4 === 'string' ? r.key_last4 : apiKey.slice(-4),
		saved_at: typeof r.saved_at === 'string' ? r.saved_at : '',
	};
}

// personal 區只硬驗 access_token/refresh_token/expires_at（鏡像 enterprise 只硬驗 api_key 的慣例）；
// 其餘顯示用欄位 soft-default 成空字串，不因為缺 email/display_name 就把整份憑證判死。
function parsePersonalCredentials(raw: unknown): PersonalCredentials {
	const path = getCredentialsPath();
	if (
		!raw ||
		typeof raw !== 'object' ||
		typeof (raw as Record<string, unknown>).access_token !== 'string' ||
		typeof (raw as Record<string, unknown>).refresh_token !== 'string' ||
		typeof (raw as Record<string, unknown>).expires_at !== 'string'
	) {
		throw new CliError(
			`Personal credentials at ${path} are malformed or incomplete. Run "wport login" to sign in again.`,
			ExitCode.ConfigCorrupt
		);
	}
	const r = raw as Record<string, unknown>;
	return {
		access_token: r.access_token as string,
		refresh_token: r.refresh_token as string,
		expires_at: r.expires_at as string,
		display_name: typeof r.display_name === 'string' ? r.display_name : '',
		email: typeof r.email === 'string' ? r.email : '',
		session_created_at: typeof r.session_created_at === 'string' ? r.session_created_at : '',
	};
}

// Atomic write 模式照抄 config-store.saveConfig：tmpfile(0o600) → rename。
// credentials 比 config 更敏感，所以分檔（config 可能被使用者貼進 issue 除錯）。
function writeFileAtomic0600(path: string, content: string): void {
	mkdirSync(dirname(path), { recursive: true });
	const tmpPath = `${path}.tmp.${process.pid}.${Date.now()}`;
	const fd = openSync(tmpPath, 'w', 0o600);
	try {
		writeSync(fd, content);
	} catch (err) {
		closeSync(fd);
		try {
			unlinkSync(tmpPath);
		} catch {
			/* best effort cleanup */
		}
		throw err;
	}
	closeSync(fd);
	if (process.platform !== 'win32') {
		try {
			chmodSync(tmpPath, 0o600);
		} catch (err) {
			printWarn(
				`Failed to chmod 0600 on credentials tmpfile: ${(err as Error).message}. ` +
					'Other users on this system may be able to read your API key.',
				false
			);
		}
	}
	renameSync(tmpPath, path);
}

/** file 中未指定的區塊 = undefined = 不寫入該欄位（v2 殼本身仍會寫出）。 */
function writeCredentialsFile(file: RawCredentialsFile): void {
	const body: Record<string, unknown> = { version: 2 };
	if (file.enterprise !== undefined) body.enterprise = file.enterprise;
	if (file.personal !== undefined) body.personal = file.personal;
	writeFileAtomic0600(getCredentialsPath(), JSON.stringify(body, null, 2) + '\n');
}

export function loadCredentials(): Credentials | null {
	const { enterprise } = readRawFile(true);
	if (enterprise === undefined) return null;
	return parseEnterpriseCredentials(enterprise);
}

export function saveCredentials(creds: Credentials): void {
	const { personal } = readRawFile(false);
	writeCredentialsFile({ enterprise: creds, personal });
}

export function deleteCredentials(): boolean {
	const path = getCredentialsPath();
	if (!existsSync(path)) return false;
	const { enterprise, personal } = readRawFile(false);
	// 兩區都解析不出來（JSON 壞損、或合法 JSON 但無可辨識的 enterprise/personal 結構）：
	// 維持 Task1 不變量「既有 export 維持不變（enterprise 命令零改動）」——舊版 deleteCredentials
	// 從不解析內容，檔案存在就整個刪除並回 true。這裡沒有 personal 資料要保留，直接比照辦理，
	// 否則 `wport enterprise logout` 對壞掉的檔案會靜默回 false 且留下無法復原的殘骸。
	if (enterprise === undefined && personal === undefined) {
		unlinkSync(path);
		return true;
	}
	if (enterprise === undefined) return false;
	// 兩區都空了才整檔刪除；personal 還在就只清 enterprise 區、保留 personal（見既有
	// 'deleteCredentials returns true when file existed, false otherwise' 測試：單區時檔案要整個消失）。
	if (personal === undefined) {
		unlinkSync(path);
	} else {
		writeCredentialsFile({ personal });
	}
	return true;
}

export function loadPersonalCredentials(): PersonalCredentials | null {
	const { personal } = readRawFile(true);
	if (personal === undefined) return null;
	return parsePersonalCredentials(personal);
}

export function savePersonalCredentials(p: PersonalCredentials): void {
	const { enterprise } = readRawFile(false);
	writeCredentialsFile({ enterprise, personal: p });
}

export function deletePersonalCredentials(): boolean {
	const { enterprise, personal } = readRawFile(false);
	if (personal === undefined) return false;
	writeCredentialsFile({ enterprise });
	return true;
}

/**
 * Key 解析 precedence：--api-key flag > WPORT_API_KEY env > credentials.json。
 * 與 base url 解析（--api > WPORT_API_BASE > default，global-opts.ts）同款心智模型。
 */
export function resolveApiKey(flagValue?: string): ResolvedKey {
	if (flagValue !== undefined) {
		ensureFormat(flagValue, '--api-key');
		return { key: flagValue, source: 'flag' };
	}
	const fromEnv = process.env[API_KEY_ENV_VAR]?.trim();
	if (fromEnv) {
		ensureFormat(fromEnv, `${API_KEY_ENV_VAR} env var`);
		return { key: fromEnv, source: 'env' };
	}
	const creds = loadCredentials();
	if (creds) return { key: creds.api_key, source: 'file' };
	throw new CliError(
		`No API key found. Run "wport enterprise login" or set the ${API_KEY_ENV_VAR} env var.`,
		ExitCode.InvalidArgument
	);
}

function ensureFormat(key: string, source: string): void {
	if (!isValidKeyFormat(key)) {
		// 錯誤訊息絕不 echo key 原文（可能是手滑貼進來的其他 secret）
		throw new CliError(`API key from ${source} is not a valid ${KEY_PREFIX} key`, ExitCode.InvalidArgument);
	}
}
